import { useEffect, useRef, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSource } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import type { Library } from "../../../sources/types";
import { useServers } from "../../../lib/servers";
import { useLibraryScanState } from "../../../lib/useServerEvents";
import { useServerActivity, type ScanProgress } from "../../../lib/activityHub";
import { describeCancelError } from "../../../lib/inProgress";
import {
  DEFAULT_SETTINGS,
  cancelLibraryRefresh,
  cancelLibraryScan,
  deleteLibrary,
  emptyTrash,
  libraryItemCount,
  refreshLibrary,
  providersLabel,
  providersOf,
  scanAllLibraries,
  scanLibrary,
  type AdminLibrary,
} from "../../../lib/libraryAdmin";
import { Chip, ErrorNote, Spinner } from "../../../components/ui";
import {
  SheetHeader,
  SheetInset,
  SheetMessage,
  SheetRow,
  SheetRows,
  SheetSection,
} from "../../../components/settings-sheet";
import { ConfirmDialog } from "../../../components/modal";
import { AddLibraryWizard, EditLibraryDialog } from "../../../components/library-wizard";
import { DotsVerticalIcon, FilmstripIcon, PlusIcon, TvIcon } from "../../../components/icons";
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from "../../../components/menu";
import { ServerSettingsGuard } from "../../../components/server-settings";

export const Route = createFileRoute("/_authed/settings/server/$serverId/libraries")({
  validateSearch: (s: Record<string, unknown>): { setup?: number } =>
    s.setup ? { setup: 1 } : {},
  component: ServerLibraries,
});

function ServerLibraries() {
  const { serverId } = Route.useParams();
  const { setup } = Route.useSearch();
  const navigate = useNavigate();
  const { servers } = useServers();
  const server = servers.find((s) => s.id === serverId);
  const source = getSource(serverId);
  const isServer = source instanceof ServerSource;
  const server_ = isServer ? source : null;

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AdminLibrary | null>(null);
  const [deleting, setDeleting] = useState<Library | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<unknown>(null);
  const [rowError, setRowError] = useState<unknown>(null);
  const [scanningAll, setScanningAll] = useState(false);
  const qc = useQueryClient();

  const libraries = useQuery({
    queryKey: ["settings", "libraries", serverId],
    staleTime: 30_000,
    enabled: Boolean(source) && isServer && Boolean(server?.online),
    queryFn: () => source!.getLibraries(),
  });

  const { scanning, socketOk } = useLibraryScanState(
    isServer && server?.online ? serverId : null,
  );
  // Live scan progress (same hub subscription): its `done` ticks gate the
  // per-row item-count refetches, so counts only fetch while a scan is
  // actually moving.
  const activity = useServerActivity(isServer && server?.online ? serverId : null);

  // The claim flow and the server-hosted setup wizard both deep-link here
  // with ?setup=1 to mean "the user has nothing yet — open the wizard".
  useEffect(() => {
    if (!setup || !isServer || !server?.online) return;
    setAdding(true);
    void navigate({ to: ".", search: {}, replace: true });
  }, [setup, isServer, server?.online, navigate]);

  async function runAction(action: () => Promise<unknown>) {
    setRowError(null);
    try {
      await action();
    } catch (e) {
      setRowError(e);
    }
  }

  // "Scan library files" above the list: every library, top to bottom, one
  // completely before the next (the server's queue rule). Disabled while
  // the queue has anything running, waiting or held — Emby's guard against
  // a second run; the per-library menus keep their own Scan / Cancel.
  const queueBusy =
    activity.scanning.size > 0 || activity.queued.size > 0 || activity.draining !== null;
  async function scanAll() {
    if (!server_) return;
    setScanningAll(true);
    try {
      await runAction(() => scanAllLibraries(server_.conn));
    } finally {
      setScanningAll(false);
    }
  }

  async function confirmDelete() {
    if (!deleting || !isServer) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteLibrary(source.conn, deleting.id);
      setDeleting(null);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["settings", "libraries", serverId] }),
        qc.invalidateQueries({ queryKey: ["nav", "servers"] }),
        qc.invalidateQueries({ queryKey: ["hubs"] }),
        qc.invalidateQueries({ queryKey: ["resources"] }),
      ]);
    } catch (e) {
      setDeleteError(e);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <ServerSettingsGuard serverId={serverId} heading="Libraries">
      {(srv) => (
        <div>
          <SheetHeader
            title="Libraries"
            action={
              <>
                {(libraries.data ?? []).length > 0 ? (
                  <button
                    type="button"
                    className="rago-btn rago-btn-quiet rago-btn-sm"
                    disabled={scanningAll || queueBusy}
                    title={queueBusy ? "A scan is already running" : undefined}
                    onClick={() => void scanAll()}
                  >
                    {scanningAll ? <Spinner size={14} /> : null}
                    Scan library files
                  </button>
                ) : null}
                <button
                  type="button"
                  className="rago-btn rago-btn-sm"
                  onClick={() => setAdding(true)}
                >
                  <PlusIcon size={14} />
                  Add library
                </button>
              </>
            }
          />

          <>
              {rowError ? (
                <SheetInset>
                  <ErrorNote error={rowError} />
                </SheetInset>
              ) : null}
              <SheetSection flush>
                {libraries.isLoading ? (
                  <SheetMessage loading>Loading…</SheetMessage>
                ) : (libraries.data ?? []).length === 0 ? (
                  <SheetMessage>No libraries yet — add one to start scanning.</SheetMessage>
                ) : (
                  <SheetRows>
                    {(libraries.data ?? []).map((lib) => (
                      <LibraryRow
                        key={lib.id}
                        library={lib}
                        source={server_}
                        scanning={scanning.has(lib.id)}
                        queued={activity.queued.get(lib.id)}
                        progress={activity.scanning.get(lib.id)}
                        refreshing={(activity.refreshing.get(lib.id) ?? 0) > 0}
                        draining={activity.draining?.libraryId === lib.id}
                        socketOk={socketOk}
                        progressDone={activity.scanning.get(lib.id)?.done}
                        onEdit={() => setEditing(toAdminLibrary(lib))}
                        onScan={() =>
                          void runAction(() => scanLibrary(server_!.conn, lib.id))
                        }
                        onCancelScan={() =>
                          void runAction(() =>
                            cancelLibraryScan(server_!.conn, lib.id).catch((e: unknown) => {
                              throw describeCancelError(e);
                            }),
                          )
                        }
                        onRefresh={() =>
                          void runAction(() => refreshLibrary(server_!.conn, lib.id))
                        }
                        onCancelRefresh={() =>
                          void runAction(() =>
                            cancelLibraryRefresh(server_!.conn, lib.id).catch((e: unknown) => {
                              throw describeCancelError(e);
                            }),
                          )
                        }
                        onEmptyTrash={() =>
                          void runAction(async () => {
                            await emptyTrash(server_!.conn, lib.id);
                            await qc.invalidateQueries({
                              queryKey: ["settings", "libraries", serverId],
                            });
                          })
                        }
                        onDelete={() => {
                          setDeleteError(null);
                          setDeleting(lib);
                        }}
                      />
                    ))}
                  </SheetRows>
                )}
              </SheetSection>

              {isServer ? (
                <>
                  <AddLibraryWizard
                    source={source}
                    serverId={serverId}
                    open={adding}
                    onClose={() => setAdding(false)}
                  />
                  {editing ? (
                    <EditLibraryDialog
                      source={source}
                      serverId={serverId}
                      library={editing}
                      open
                      onClose={() => setEditing(null)}
                    />
                  ) : null}
                </>
              ) : null}

              <ConfirmDialog
                open={deleting !== null}
                title={`Delete ${deleting?.title ?? "library"}?`}
                body={
                  <>
                    Its items disappear from Home, search and the library list, along with their
                    watch history. <strong className="text-[var(--rago-text-1)]">Your files
                    are not touched</strong> — re-adding the folder rebuilds the library.
                  </>
                }
                confirmLabel="Delete library"
                danger
                busy={deleteBusy}
                error={deleteError}
                onConfirm={() => void confirmDelete()}
                onCancel={() => setDeleting(null)}
              />
          </>
        </div>
      )}
    </ServerSettingsGuard>
  );
}

function toAdminLibrary(lib: Library): AdminLibrary {
  return {
    id: lib.id,
    type: lib.type,
    title: lib.title,
    language: lib.language,
    itemCount: lib.itemCount,
    agent: lib.agent,
    providers: lib.providers,
    locations: lib.locations,
    settings: { ...DEFAULT_SETTINGS, ...lib.settings },
  };
}

function LibraryRow(props: {
  library: Library;
  source: ServerSource | null;
  scanning: boolean;
  /** Waiting in the scan queue (with the reason the job carries). */
  queued?: { reason?: string };
  /** The running scan's live progress (stage, counts, reason). */
  progress?: ScanProgress;
  /** Items of this library still waiting for the refresh worker. */
  refreshing: boolean;
  /** The scan queue holds this library after its scan while its metadata
   * is fetched — the next queued library starts when it is done. */
  draining: boolean;
  socketOk: boolean;
  /** Live scan progress counter — its movement drives count refetches. */
  progressDone?: number;
  onEdit: () => void;
  onScan: () => void;
  onCancelScan: () => void;
  onRefresh: () => void;
  onCancelRefresh: () => void;
  /** The post-scan trash sweep on demand (409 while the library scans). */
  onEmptyTrash: () => void;
  onDelete: () => void;
}) {
  const { library: lib } = props;
  // Action deferred until the kebab menu has fully closed (see MenuContent).
  const pending = useRef<(() => void) | null>(null);
  const liveCount = useScanCount(
    props.source,
    props.scanning ? lib.id : null,
    props.socketOk,
    props.progressDone,
  );
  const count = liveCount ?? lib.itemCount;
  const Icon = lib.type === "show" ? TvIcon : FilmstripIcon;

  // A scan queued or running with reason `refresh` is the walk half of
  // Refresh all metadata: it cancels through the refresh. Kenny: "if it is
  // scanning already the 'scan library' should be replaced with cancel
  // scan" — same slot, plain item; Delete stays the only red one.
  const refreshScan = props.progress?.reason === "refresh" || props.queued?.reason === "refresh";
  const scanFlow = (props.scanning || props.queued !== undefined) && !refreshScan;
  const refreshFlow = props.refreshing || refreshScan || props.draining;
  const parts: string[] = [lib.type === "show" ? "TV Shows" : "Movies"];
  if (count !== undefined) parts.push(`${count} item${count === 1 ? "" : "s"}`);
  const [first, ...rest] = lib.locations ?? [];
  if (first) parts.push(rest.length > 0 ? `${first} +${rest.length} more` : first);

  return (
    <SheetRow>
      <span className="flex min-w-0 items-center gap-3">
        <Icon className="shrink-0 text-[var(--rago-text-2)]" />
        <span className="min-w-0 leading-tight">
          <span className="block truncate text-sm text-[var(--rago-text-1)]">{lib.title}</span>
          <span className="block truncate text-xs text-[var(--rago-text-3)]">
            {parts.join(" · ")}
          </span>
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-2">
        {props.scanning ? (
          <Chip tone="accent">
            {props.progress && props.progress.total > 0
              ? `Scanning ${props.progress.done.toLocaleString("en-US")}/${props.progress.total.toLocaleString("en-US")}`
              : "Scanning…"}
          </Chip>
        ) : props.draining ? (
          <Chip tone="accent">Updating metadata</Chip>
        ) : props.queued ? (
          <Chip tone="muted">Queued</Chip>
        ) : null}
        {lib.agent || lib.providers ? (
          <span className="hidden sm:inline">
            <Chip tone="muted">{providersLabel(providersOf(lib))}</Chip>
          </span>
        ) : null}
        <Menu>
          <MenuTrigger
            aria-label={`Actions for ${lib.title}`}
            className="rounded-[var(--rago-radius-s)] p-1.5 text-[var(--rago-text-3)] transition-colors hover:text-[var(--rago-text-1)] data-[state=open]:text-[var(--rago-text-1)]"
          >
            <DotsVerticalIcon size={18} />
          </MenuTrigger>
          <MenuContent
            align="end"
            width="sm"
            // Edit/Delete open dialogs: run them only once the menu has closed,
            // otherwise Radix's focus-return to the kebab (after the 100ms exit
            // animation) would steal focus from the freshly opened dialog.
            onCloseAutoFocus={() => {
              const run = pending.current;
              pending.current = null;
              run?.();
            }}
          >
            <MenuItem asChild>
              <Link
                to="/library/$sourceId/$libraryId"
                params={{ sourceId: lib.sourceId, libraryId: lib.id }}
                search={{ sort: "added" }}
              >
                Open
              </Link>
            </MenuItem>
            <MenuItem onSelect={() => { pending.current = props.onEdit; }}>Edit</MenuItem>
            {scanFlow ? (
              <MenuItem onSelect={props.onCancelScan}>Cancel scan</MenuItem>
            ) : (
              <MenuItem onSelect={props.onScan}>Scan library files</MenuItem>
            )}
            {refreshFlow ? (
              <MenuItem onSelect={props.onCancelRefresh}>Cancel refresh</MenuItem>
            ) : (
              <MenuItem onSelect={props.onRefresh}>Refresh metadata</MenuItem>
            )}
            <MenuItem onSelect={props.onEmptyTrash}>Empty trash</MenuItem>
            <MenuSeparator />
            <MenuItem danger onSelect={() => { pending.current = props.onDelete; }}>
              Delete
            </MenuItem>
          </MenuContent>
        </Menu>
      </span>
    </SheetRow>
  );
}

/**
 * The live item count while a library scans. Events tell us WHEN a scan
 * runs AND when it moves: with a working socket each `status` progress
 * tick (passed in as progressDone) gates one count fetch per ~2 s, so
 * nothing polls during provider stalls or between file batches. When the
 * socket never opened (relay without WebSocket upgrade), the old blind
 * 2-second loop returns, and a stabilizing count is the fallback signal
 * that the scan is done.
 */
function useScanCount(
  source: ServerSource | null,
  libraryId: string | null,
  socketOk: boolean,
  progressDone?: number,
): number | undefined {
  const [count, setCount] = useState<number | undefined>(undefined);
  const qc = useQueryClient();
  const lastFetch = useRef(0);

  useEffect(() => {
    if (!source || !libraryId) {
      setCount(undefined);
      lastFetch.current = 0;
      return;
    }
    if (socketOk) {
      // Event-gated: this effect re-runs on every progress tick; the
      // timestamp guard turns that into at most one fetch per 2 s.
      const now = Date.now();
      if (lastFetch.current !== 0 && now - lastFetch.current < 2000) return;
      lastFetch.current = now;
      let cancelled = false;
      libraryItemCount(source.conn, libraryId).then(
        (total) => {
          if (!cancelled) setCount(total);
        },
        () => {
          /* transient — the next progress tick retries */
        },
      );
      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;
    let stable = 0;
    let last = -1;

    const tick = async () => {
      if (cancelled) return;
      try {
        const total = await libraryItemCount(source.conn, libraryId);
        if (cancelled) return;
        setCount(total);
        stable = total === last ? stable + 1 : 0;
        last = total;
        // Without events, three identical reads is our "scan finished".
        if (stable >= 3) {
          void qc.invalidateQueries({ queryKey: ["settings", "libraries", source.id] });
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      if (!cancelled) timer = setTimeout(() => void tick(), 2000);
    };
    let timer = setTimeout(() => void tick(), 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [source, libraryId, socketOk, progressDone, qc]);

  return libraryId ? count : undefined;
}
